import { normalizeWebOtaVersionToken } from '@/helpers/normalizeWebOtaVersionToken'

/** Legacy single-shot flag; cleared when attempts helpers reset. */
export const WEB_OTA_BROWSER_CACHE_BUST_TRIED_KEY = 'lucimark_web_ota_cache_bust_tried'

const WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY = 'lucimark_web_ota_reload_attempts'
const WEB_OTA_BROWSER_RELOAD_OFFERED_KEY = 'lucimark_web_ota_reload_offered'

/** Soft clear + reload, then unregister + reload before giving up. */
export const WEB_OTA_BROWSER_MAX_RELOAD_ATTEMPTS = 2

function normalizeOffered(offered: string): string {
  return normalizeWebOtaVersionToken(offered).toLowerCase()
}

/** Attempt count for this offered bundle id (resets when the server offers a new version). */
export function getWebOtaBrowserReloadAttempts(offeredVersion: string): number {
  if (typeof window === 'undefined') {
    return 0
  }
  const offered = normalizeOffered(offeredVersion)
  if (offered === '') {
    return 0
  }
  try {
    const lastOffered = window.sessionStorage.getItem(WEB_OTA_BROWSER_RELOAD_OFFERED_KEY) ?? ''
    if (lastOffered !== offered) {
      window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_OFFERED_KEY, offered)
      window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY, '0')
      return 0
    }
    const raw = window.sessionStorage.getItem(WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY)
    const n = raw != null ? Number.parseInt(raw, 10) : 0
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  catch {
    return 0
  }
}

export function incrementWebOtaBrowserReloadAttempts(offeredVersion: string): number {
  const next = getWebOtaBrowserReloadAttempts(offeredVersion) + 1
  if (typeof window === 'undefined') {
    return next
  }
  const offered = normalizeOffered(offeredVersion)
  try {
    window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_OFFERED_KEY, offered)
    window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY, String(next))
  }
  catch {
    // ignore
  }
  return next
}

export function clearWebOtaBrowserReloadSession(): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.removeItem(WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY)
    window.sessionStorage.removeItem(WEB_OTA_BROWSER_RELOAD_OFFERED_KEY)
    window.sessionStorage.removeItem(WEB_OTA_BROWSER_CACHE_BUST_TRIED_KEY)
  }
  catch {
    // ignore
  }
}

/** Undo the last increment when a scheduled reload was superseded before navigation. */
export function rollbackWebOtaBrowserReloadAttempt(offeredVersion: string): void {
  const current = getWebOtaBrowserReloadAttempts(offeredVersion)
  if (current <= 0 || typeof window === 'undefined') {
    return
  }
  const offered = normalizeOffered(offeredVersion)
  try {
    window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_OFFERED_KEY, offered)
    window.sessionStorage.setItem(WEB_OTA_BROWSER_RELOAD_ATTEMPTS_KEY, String(current - 1))
  }
  catch {
    // ignore
  }
}
